<template>
  <div id="links-section" v-if="!loading && visibleItems.length">
    <template v-for="item in visibleItems">

      <!-- Section header -->
      <p
        v-if="item.type === 'header'"
        :key="`h-${item.id}`"
        class="links-headline mute tt-up ta-c fs-0 m-t-xl"
      >{{ item.title }}</p>
      
      <!-- Link row -->
      <a
        v-else
        :key="`l-${item.id}`"
        :href="normalizeUrl(item.url)"
        target="_blank"
        rel="noopener"
        class="link-row d-f ai-c b-bottom bs-bb"
      >
        <div class="link-thumb">
          <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title" />
          <div v-else class="link-thumb-placeholder d-f ai-c jc-c">
            <icon-base :w="15" :h="15">
              <icon-link />
            </icon-base>
          </div>
        </div>
        <div class="link-text d-f fd-c flex-1" style="min-width:0">
          <span class="fw-b">{{ item.title }}</span>
          <span v-if="item.subCopy" class="mute fs-0">{{ item.subCopy }}</span>
        </div>
        <icon-base :w="15" :h="15">
          <icon-arrow-up-right />
        </icon-base>
      </a>

    </template>
  </div>
</template>

<script>
import { rtdb } from '@/db.js';
import IconBase from '@/assets/icons/IconBase.vue';
import IconArrowUpRight from '@/assets/icons/IconArrowUpRight2.vue';
import IconLink from '@/assets/icons/IconLink.vue';

export default {
  name: 'LinksSection',
  components: { IconBase, IconArrowUpRight, IconLink },
  props: {
    userId: { type: String, required: true },
  },
  data() {
    return {
      items: [],
      loading: true,
      _ref: null,
    };
  },
  mounted() {
    this.fetchItems();
  },
  beforeDestroy() {
    if (this._ref) this._ref.off();
  },
  watch: {
    userId(val) {
      if (!val) return;
      if (this._ref) this._ref.off();
      this.fetchItems();
    },
  },
  computed: {
    visibleItems() {
      const sorted = [...this.items]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter(item => item.type === 'header' || item.published);

      // Remove orphaned section headers (no link follows before next header or end)
      return sorted.filter((item, i) => {
        if (item.type !== 'header') return true;
        for (let j = i + 1; j < sorted.length; j++) {
          if (sorted[j].type === 'header') break;
          if (sorted[j].type === 'link') return true;
        }
        return false;
      });
    },
  },
  methods: {
    fetchItems() {
      this.loading = true;
      this._ref = rtdb.ref(`users/${this.userId}/customLinks`);
      this._ref.on('value', snapshot => {
        const val = snapshot.val() || {};
        this.items = Object.entries(val).map(([id, data]) => ({ id, ...data }));
        this.loading = false;
      });
    },
    normalizeUrl(url) {
      if (!url) return '#';
      return /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

#links-section {
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;

  .link-row {
    gap: $mg-desktop;
    text-decoration: none;
    transition: opacity 0.15s ease;
    padding-top: $mg-desktop-sm;
    padding-bottom: $mg-desktop-sm;
    margin-top: $mg-desktop;

    &:hover { opacity: 0.7; }

    @media #{$mobile} {
      gap: $mg-desktop;
    }
  }

  .link-thumb {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .link-thumb-placeholder {
      width: 100%;
      height: 100%;
      background-color: $black;
    }

    @media #{$mobile} {
      width: 50px;
      height: 50px;
    }
  }

  .link-text {
    padding: $mg-desktop-sm 0;
  }
}
</style>
